<script setup lang="ts">
   import { computed } from 'vue';
   import type { TournamentEntryFieldInput } from '~/types/types';

   const props = defineProps<{
      field: TournamentEntryFieldInput;
      modelValue: string | number | undefined;
      error?: string;
      disabled?: boolean;
   }>();

   const emit = defineEmits<{
      'update:modelValue': [value: string | number];
   }>();

   const inputId = computed(() => `te-field-${props.field.name}`);

   const onInput = (event: Event) => {
      const target = event.target as HTMLInputElement | HTMLTextAreaElement;
      if (props.field.type === 'number') {
         emit('update:modelValue', target.value === '' ? '' : Number(target.value));
         return;
      }
      emit('update:modelValue', target.value);
   };

   const baseInputClass =
      'w-full rounded-md border bg-white px-3 py-2 text-base text-gray-900 focus:outline-none focus:ring-2 focus:ring-cyan-500/20 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed';
</script>

<template>
   <div class="mb-4 flex flex-col gap-1.5">
      <label :for="inputId" class="text-sm font-semibold text-gray-800">
         {{ field.label }}
         <span v-if="field.required" class="ml-0.5 text-red-700" aria-hidden="true">*</span>
      </label>

      <textarea
         v-if="field.type === 'textarea'"
         :id="inputId"
         :value="modelValue as string"
         :required="field.required"
         :placeholder="field.placeholder"
         :disabled="disabled"
         rows="4"
         :class="[baseInputClass, error ? 'border-red-700' : 'border-gray-300 focus:border-cyan-500']"
         @input="onInput" />

      <input
         v-else
         :id="inputId"
         :type="field.type"
         :value="modelValue"
         :required="field.required"
         :placeholder="field.placeholder"
         :disabled="disabled"
         :class="[baseInputClass, error ? 'border-red-700' : 'border-gray-300 focus:border-cyan-500']"
         @input="onInput" />

      <p v-if="error" class="text-xs text-red-700">{{ error }}</p>
   </div>
</template>
